import { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  surfaceHover: "#1a2234",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  textDim: "#475569",
  error: "#f87171",
  errorBg: "rgba(248, 113, 113, 0.08)",
};

function ResultsTable({ results }) {
  // results is array-of-objects: [{col: val, ...}]
  if (!results || results.length === 0) return null;
  const columns = Object.keys(results[0]);
  return (
    <div style={{ overflowX: "auto", marginTop: 10 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                style={{
                  textAlign: "left",
                  padding: "7px 10px",
                  color: theme.accent,
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  borderBottom: `1px solid ${theme.border}`,
                  background: theme.bg,
                  whiteSpace: "nowrap",
                }}
              >
                {col.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td
                  key={col}
                  style={{
                    padding: "7px 10px",
                    borderBottom: `1px solid ${theme.border}44`,
                    color: theme.text,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row[col] === null || row[col] === undefined ? (
                    <span style={{ color: theme.textDim }}>—</span>
                  ) : (
                    String(row[col])
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceQueryItem({ index, prompt, queryResult, queryMeta }) {
  const [showResults, setShowResults] = useState(false);

  const hasResults = queryResult?.results?.length > 0;
  const hasSql = queryResult?.sql || queryMeta?.sql;
  const sql = queryResult?.sql || queryMeta?.sql;
  const rowCount = queryResult?.results?.length ?? queryMeta?.row_count;

  return (
    <div
      style={{
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        overflow: "hidden",
        marginBottom: 6,
        fontFamily: "'IBM Plex Mono', monospace",
      }}
    >
      {/* Prompt row — always visible, clickable to expand results */}
      <div
        style={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <span style={{ color: theme.accent, fontWeight: 700, minWidth: 16, flexShrink: 0 }}>
          {index + 1}.
        </span>
        <span style={{ flex: 1, fontSize: 13, color: theme.text, lineHeight: 1.5 }}>
          {prompt}
        </span>
        {(hasResults || hasSql) && (
          <button
            onClick={() => setShowResults((v) => !v)}
            style={{
              background: "none",
              border: "none",
              color: showResults ? theme.accent : theme.textMuted,
              fontSize: 11,
              fontFamily: "'IBM Plex Mono', monospace",
              cursor: "pointer",
              padding: "2px 0",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              gap: 4,
              transition: "color 0.15s",
            }}
          >
            <span
              style={{
                fontSize: 9,
                display: "inline-block",
                transition: "transform 0.2s",
                transform: showResults ? "rotate(90deg)" : "rotate(0deg)",
              }}
            >
              ▶
            </span>
            {hasResults ? "Results" : "SQL"}
            {rowCount != null && (
              <span style={{ color: theme.textDim }}>({rowCount})</span>
            )}
          </button>
        )}
      </div>

      {/* Expanded: results table for session vizzes, SQL for DB vizzes */}
      {showResults && (
        <div style={{ borderTop: `1px solid ${theme.border}` }}>
          {hasResults ? (
            <div style={{ padding: "0 12px 12px" }}>
              <ResultsTable results={queryResult.results} />
            </div>
          ) : (
            <div
              style={{
                padding: "10px 12px",
                fontSize: 12,
                color: theme.accent,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                background: theme.bg,
                lineHeight: 1.6,
              }}
            >
              {sql}
              {queryMeta?.row_count != null && (
                <div style={{ color: theme.textDim, marginTop: 8 }}>
                  {queryMeta.row_count} row{queryMeta.row_count !== 1 ? "s" : ""} returned
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VizCard({ viz, onDelete, session }) {
  const [currentHtml, setCurrentHtml] = useState(viz.html);
  const [expanded, setExpanded] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refinement, setRefinement] = useState("");
  const [refineLoading, setRefineLoading] = useState(false);
  const [refineError, setRefineError] = useState(null);

  const handleRefine = async () => {
    if (!refinement.trim()) return;
    setRefineLoading(true);
    setRefineError(null);
    try {
      const res = await fetch(`${API_URL}/visualize/refine`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          visualization_id: viz.id,
          original_html: currentHtml,
          refinement: refinement.trim(),
          source_queries: viz.source_queries,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRefineError(
          res.status === 429
            ? "Too many requests. Please wait a moment."
            : data.detail || "Failed to regenerate. Try again."
        );
      } else {
        setCurrentHtml(data.html);
        setRefinement("");
        setShowRefine(false);
      }
    } catch {
      setRefineError("Could not connect to the server.");
    } finally {
      setRefineLoading(false);
    }
  };

  const formattedDate = viz.created_at
    ? new Date(viz.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div
      style={{
        background: theme.surface,
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        overflow: "hidden",
        marginBottom: 20,
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 20px",
          borderBottom: `1px solid ${theme.border}`,
          background: `linear-gradient(135deg, ${theme.accentDim}, transparent)`,
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 14 }}>📊</span>
          <span
            style={{
              fontSize: 13,
              color: theme.textMuted,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "'IBM Plex Mono', monospace",
            }}
          >
            {viz.source_queries[0]}
            {viz.source_queries.length > 1 && (
              <span style={{ color: theme.textDim }}>
                {" "}+{viz.source_queries.length - 1} more
              </span>
            )}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          {formattedDate && (
            <span style={{ fontSize: 12, color: theme.textDim, fontFamily: "'IBM Plex Mono', monospace" }}>
              {formattedDate}
            </span>
          )}
          <button
            onClick={() => { setShowRefine((v) => !v); setRefineError(null); }}
            style={{
              background: showRefine ? theme.accentDim : "none",
              border: `1px solid ${showRefine ? theme.accent : theme.border}`,
              borderRadius: 6,
              padding: "4px 10px",
              color: showRefine ? theme.accent : theme.textMuted,
              fontSize: 12,
              fontFamily: "'IBM Plex Mono', monospace",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            Refine
          </button>
          <button
            onClick={() => onDelete(viz.id)}
            style={{
              background: "none",
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              padding: "4px 10px",
              color: theme.textDim,
              fontSize: 12,
              fontFamily: "'IBM Plex Mono', monospace",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => {
              e.target.style.color = theme.error;
              e.target.style.borderColor = `${theme.error}44`;
            }}
            onMouseLeave={(e) => {
              e.target.style.color = theme.textDim;
              e.target.style.borderColor = theme.border;
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Chart iframe — sandboxed, no allow-same-origin */}
      <div style={{ padding: "16px 20px", borderBottom: `1px solid ${theme.border}` }}>
        <iframe
          sandbox="allow-scripts"
          srcDoc={currentHtml}
          style={{
            width: "100%",
            height: 450,
            border: "none",
            borderRadius: 8,
            background: theme.surfaceHover,
            display: "block",
          }}
          title="Visualization"
        />
      </div>

      {/* Inline refine panel */}
      {showRefine && (
        <div
          style={{
            padding: "14px 20px",
            borderBottom: `1px solid ${theme.border}`,
            background: theme.accentDim,
          }}
        >
          <p style={{ fontSize: 11, color: theme.accent, textTransform: "uppercase", letterSpacing: 1.2, marginTop: 0, marginBottom: 8, fontWeight: 600, fontFamily: "'IBM Plex Mono', monospace" }}>
            Refine this visualization
          </p>
          <textarea
            value={refinement}
            onChange={(e) => setRefinement(e.target.value)}
            disabled={refineLoading}
            placeholder="e.g. 'Add labels to each data point', 'Switch to a bar chart', 'Sort bars by value descending'"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              color: refineLoading ? theme.textDim : theme.text,
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              padding: "10px 12px",
              resize: "vertical",
              minHeight: 64,
              outline: "none",
              lineHeight: 1.6,
            }}
            onFocus={(e) => { e.target.style.borderColor = theme.accent; }}
            onBlur={(e) => { e.target.style.borderColor = theme.border; }}
          />
          {refineError && (
            <p style={{ fontSize: 12, color: theme.error, margin: "6px 0 0", fontFamily: "'IBM Plex Mono', monospace" }}>
              {refineError}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
            <button
              onClick={() => { setShowRefine(false); setRefinement(""); setRefineError(null); }}
              disabled={refineLoading}
              style={{ background: "none", border: `1px solid ${theme.border}`, borderRadius: 6, padding: "6px 14px", color: theme.textMuted, fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", cursor: refineLoading ? "not-allowed" : "pointer", opacity: refineLoading ? 0.5 : 1 }}
            >
              Cancel
            </button>
            <button
              onClick={handleRefine}
              disabled={refineLoading || !refinement.trim()}
              style={{ background: refineLoading || !refinement.trim() ? theme.accentDim : `linear-gradient(135deg, ${theme.accent}, #00c98a)`, border: "none", borderRadius: 6, padding: "6px 16px", color: refineLoading || !refinement.trim() ? theme.accent : theme.bg, fontSize: 12, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", cursor: refineLoading || !refinement.trim() ? "not-allowed" : "pointer" }}
            >
              {refineLoading ? "Regenerating..." : "Regenerate"}
            </button>
          </div>
        </div>
      )}

      {/* Source queries collapsible */}
      <div style={{ padding: "0 20px" }}>
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: "none",
            border: "none",
            color: theme.textMuted,
            fontSize: 12,
            fontFamily: "'IBM Plex Mono', monospace",
            cursor: "pointer",
            padding: "12px 0",
            display: "flex",
            alignItems: "center",
            gap: 6,
            width: "100%",
            textAlign: "left",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = theme.accent)}
          onMouseLeave={(e) => (e.currentTarget.style.color = theme.textMuted)}
        >
          <span
            style={{
              fontSize: 10,
              transition: "transform 0.2s",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              display: "inline-block",
            }}
          >
            ▶
          </span>
          Source {viz.source_queries.length === 1 ? "Query" : `Queries (${viz.source_queries.length})`}
        </button>

        {expanded && (
          <div style={{ paddingBottom: 16 }}>
            {viz.source_queries.map((prompt, i) => (
              <SourceQueryItem
                key={i}
                index={i}
                prompt={prompt}
                queryResult={viz.queryResults?.[i] ?? null}
                queryMeta={viz.sourceQueryMeta?.[i] ?? null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VisualizationsPanel({ sessionVisualizations, onDeleteSession }) {
  const { user, session } = useAuth();
  const [dbVisualizations, setDbVisualizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (user) fetchVisualizations();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVisualizations = async () => {
    setLoading(true);
    setFetchError(null);
    const { data, error } = await supabase
      .from("visualizations")
      .select("id, html_content, source_queries, created_at")
      .order("created_at", { ascending: false });
    if (error) {
      setFetchError("Failed to load saved visualizations.");
    } else {
      setDbVisualizations(data || []);
    }
    setLoading(false);
  };

  const deleteFromDb = async (id) => {
    await supabase.from("visualizations").delete().eq("id", id);
    setDbVisualizations((prev) => prev.filter((v) => v.id !== id));
  };

  // Normalize session vizzes — carry full query results from the moment of generation
  const sessionNormalized = sessionVisualizations.map((v) => ({
    id: v.visualization_id,
    html: v.html,
    source_queries: v.source_queries,
    // _queryResults: [{id, prompt, sql, results: [{col: val, ...}]}]
    queryResults: v._queryResults || null,
    sourceQueryMeta: null,
    created_at: v.created_at,
    isSession: true,
  }));

  const sessionIds = new Set(sessionVisualizations.map((v) => v.visualization_id));

  // Normalize DB vizzes — source_queries JSONB has [{prompt, sql, row_count, results?}]
  const dbNormalized = dbVisualizations
    .filter((v) => !sessionIds.has(v.id))
    .map((v) => {
      const meta = Array.isArray(v.source_queries)
        ? v.source_queries.filter((q) => typeof q === "object")
        : [];
      // Build queryResults from persisted results if available (new records),
      // or null for older records that predate results storage.
      const queryResults = meta.some((q) => Array.isArray(q.results))
        ? meta.map((q) => ({ prompt: q.prompt, sql: q.sql, results: q.results || [] }))
        : null;
      return {
        id: v.id,
        html: v.html_content,
        source_queries: Array.isArray(v.source_queries)
          ? v.source_queries.map((q) => (typeof q === "string" ? q : q.prompt))
          : [],
        queryResults,
        sourceQueryMeta: meta,
        created_at: v.created_at,
        isSession: false,
      };
    });

  const all = [...sessionNormalized, ...dbNormalized].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const handleDelete = (viz) => {
    if (viz.isSession) {
      onDeleteSession(viz.id);
    } else {
      deleteFromDb(viz.id);
    }
  };

  if (loading) {
    return (
      <p style={{ color: theme.textMuted, fontSize: 13, textAlign: "center", padding: "48px 0", fontFamily: "'IBM Plex Mono', monospace" }}>
        Loading...
      </p>
    );
  }

  if (all.length === 0) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "64px 24px",
          color: theme.textMuted,
          fontSize: 14,
          fontFamily: "'IBM Plex Mono', monospace",
          lineHeight: 1.8,
        }}
      >
        <div style={{ fontSize: 32, marginBottom: 16 }}>📊</div>
        <p style={{ margin: 0 }}>No visualizations yet.</p>
        <p style={{ margin: "6px 0 0", fontSize: 13, color: theme.textDim }}>
          Select query results and click <span style={{ color: theme.accent }}>Visualize</span> to generate charts.
        </p>
        {fetchError && (
          <p style={{ marginTop: 16, fontSize: 12, color: theme.error }}>
            Could not load saved visualizations.
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
      {/* Only surface the fetch error when there's nothing at all to show */}
      {fetchError && all.length === 0 && (
        <p style={{ fontSize: 12, color: theme.error, marginBottom: 16 }}>{fetchError}</p>
      )}
      {all.map((viz) => (
        <VizCard key={viz.id} viz={viz} onDelete={() => handleDelete(viz)} session={session} />
      ))}
    </div>
  );
}
